import type { ReactNode } from "react";
import { create } from "zustand/react";

import type { ModalBehaviorOptions, ModalCloseOutcome } from "./modal-behavior";

interface UserTriggeredModalsState {
  openIds: string[];
  prevOpenIds: string[];
  children: Record<string, ReactNode>;
  args: Record<string, unknown>;
  behaviorOptions: Record<string, ModalBehaviorOptions>;
  state: Record<string, unknown>;

  peekOpenIds: () => string[];
  setOpenIds: (ids: string[]) => void;
  setChildren: (id: string, children: ReactNode) => void;
  setBehaviorOptions: (id: string, options: ModalBehaviorOptions) => void;
  setArgs: (id: string, args: unknown) => void;

  peekState: (id: string) => unknown;
  setState: (id: string, value: unknown) => void;
  clearState: (id: string) => void;

  triggerCloseCallback: (id: string) => ModalCloseOutcome | void;
  getShouldConfirmClose: (id: string) => boolean | undefined;
}

export const useUserTriggeredModalsStore = create<UserTriggeredModalsState>((set, get) => ({
  openIds: [],
  prevOpenIds: [],
  children: {},
  args: {},
  behaviorOptions: {},
  state: {},

  peekOpenIds: () => get().openIds,
  setOpenIds: (ids) => set((prev) => ({ openIds: ids, prevOpenIds: prev.openIds })),
  setChildren: (id, children) =>
    set((state) => ({
      children: {
        ...state.children,
        [id]: children,
      },
    })),
  setBehaviorOptions: (id, options) => set((state) => ({ behaviorOptions: { ...state.behaviorOptions, [id]: options } })),
  setArgs: (id, args) =>
    set((state) => ({
      args: { ...state.args, [id]: args },
    })),

  peekState: (id) => get().state[id],
  setState: (id, value) => set((state) => ({ state: { ...state.state, [id]: value } })),
  clearState: (id) =>
    set((state) => ({
      state: { ...state.state, [id]: undefined },
    })),

  getShouldConfirmClose: (id) => {
    return get().behaviorOptions[id]?.requireCloseConfirmation;
  },
  triggerCloseCallback: (id) => {
    return get().behaviorOptions[id]?.onClose?.();
  },
}));
